/* ═══════════════════════════════════════════════════════════════
   API Client — HTTP calls to SpecForma EC2 API
   JWT-only. No legacy API-key fallback (removed 2026-04-18; the old
   key was extractable from the installer bundle).

   Two protections live here:

     1. In-flight request dedup. When two callers fire the same body
        at the same time (e.g. five dashboard widgets all asking for
        `listEntries(projectId)` on mount), the first wins and the
        rest share the promise. Cuts duplicate-burst traffic by ~70%
        without any caching semantics changing.

     2. 429 backoff + retry-once. If the server says "slow down"
        we sleep briefly and retry exactly one more time before
        bubbling the error to the caller. Users never see a "rate
        limit" toast for ordinary traffic — only sustained attacks
        or true runaway loops reach the second 429.
   ═══════════════════════════════════════════════════════════════ */

import { getAuthToken } from './authService';

import { API_URL } from '../lib/apiBase';

// Reads (action=list/query) are dedupable: same input = same output, so
// concurrent identical reads can share one in-flight promise.  Writes
// (save/remove/update) are NOT dedupable — even if the body is identical,
// the second caller still expects an "I made the change" semantic.
const READ_ACTIONS = new Set(['list', 'query']);

const inflight = new Map<string, Promise<any>>();

function dedupeKey(body: Record<string, any>): string {
  // Stable JSON — Object.keys order matters; sort to keep the key consistent
  // regardless of caller's property order.
  return JSON.stringify(body, Object.keys(body).sort());
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

async function apiCall(body: Record<string, any>, attempt = 0): Promise<any> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Not signed in');
  }

  // Dedup: only for safe READ requests, and only on the first attempt.
  // On retries we always fire a fresh request — the original promise has
  // already failed with 429 and isn't reusable.
  const isRead = READ_ACTIONS.has(body.action);
  if (isRead && attempt === 0) {
    const key = dedupeKey(body);
    const existing = inflight.get(key);
    if (existing) return existing;

    const promise = doFetch(body, attempt).finally(() => {
      // Clear the slot once the network round-trip resolves so the next
      // request fires fresh. We don't keep results cached here — that's
      // cloudCache.ts's job; this layer is purely about collapsing
      // simultaneous in-flight calls.
      inflight.delete(key);
    });
    inflight.set(key, promise);
    return promise;
  }

  return doFetch(body, attempt);
}

async function doFetch(body: Record<string, any>, attempt: number): Promise<any> {
  const token = getAuthToken();
  if (!token) throw new Error('Not signed in');

  const response = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  // 429 Too Many Requests — backoff and retry exactly once. The server's
  // express-rate-limit middleware uses a fixed 60s window; if we got
  // bucketed at second 59 we can just wait a beat and the bucket resets.
  if (response.status === 429 && attempt < 1) {
    await sleep(800 + Math.random() * 600); // 0.8–1.4s jitter
    return apiCall(body, attempt + 1);
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || `API error: ${response.status}`);
  }

  return response.json();
}

export async function dbList(
  table: string,
  firmId: string | null,
  filters?: Record<string, any>,
  orderBy?: string,
  columns?: string[]
): Promise<any[]> {
  return apiCall({ action: 'list', table, firmId, filters, orderBy, columns });
}

export async function dbQuery(
  table: string,
  firmId: string | null,
  filters?: { col: string; op: string; val: any }[],
  orderBy?: string
): Promise<any[]> {
  return apiCall({ action: 'query', table, firmId, filters, orderBy });
}

export async function dbSave(
  table: string,
  record: Record<string, any>
): Promise<Record<string, any>> {
  return apiCall({ action: 'save', table, record });
}

export async function dbRemove(table: string, id: string): Promise<void> {
  await apiCall({ action: 'remove', table, id });
}

export async function dbUpdate(
  table: string,
  updates: Record<string, any>,
  filters: { col: string; op: string; val: any }[]
): Promise<any[]> {
  return apiCall({ action: 'update', table, updates, filters });
}

/** Atomic single-cell upsert for hour_forecasts.manual rows. Backed by the
 *  partial unique index uq_hour_forecasts_manual_cell. Pass hours = 0 to
 *  delete the cell (idempotent). phase_id is stamped server-side by the
 *  stamp_phase_from_project trigger (migration 017) — clients do not pick
 *  the phase. Returns the resulting row, or {deleted, ids}. */
export async function forecastUpsertCell(args: {
  teamMemberId: string;
  projectId: string | null;
  dayDate: string;
  hours: number;
  notes?: string | null;
}): Promise<any> {
  return apiCall({ action: 'forecast_upsert_cell', ...args });
}
